// src/presentation/pages/ClinicalExamination/sections/SpeechSection.tsx
import React from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import LipsIcon from "@/assets/lips.svg?react";
import { useTranslation } from "react-i18next";

interface SpeechSectionProps {
  speechStatus: string;
  communicationMethods: {
    speaksIndependently: boolean;
    usesAACDevice: boolean;
    usesGestures: boolean;
  };
  onSpeechStatusChange: (value: string) => void;
  onCommunicationMethodsChange: (
    field: keyof SpeechSectionProps["communicationMethods"],
    checked: boolean,
  ) => void;
}

export const SpeechSection: React.FC<SpeechSectionProps> = ({
  speechStatus,
  communicationMethods,
  onSpeechStatusChange,
  onCommunicationMethodsChange,
}) => {
  const { t } = useTranslation("patients");
  return (
    <div className="space-y-4 pt-4">
      <div className="flex gap-2 items-center">
        <LipsIcon />
        <h4 className="text-base font-semibold text-gray-800 border-b">
          {t("clinicalSections.speech.title")}
        </h4>
      </div>

      {/* Speech Status */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">
          {t("clinicalSections.speech.statusLabel")}
        </p>
        <RadioGroup
          value={speechStatus}
          onValueChange={onSpeechStatusChange}
          className="space-y-2"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="clear" id="speech-clear" />
            <Label
              htmlFor="speech-clear"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.clear")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="mild" id="speech-mild" />
            <Label
              htmlFor="speech-mild"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.mild")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="severe" id="speech-severe" />
            <Label
              htmlFor="speech-severe"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.severe")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="non-speaking" id="speech-non" />
            <Label
              htmlFor="speech-non"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.nonSpeaking")}
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Communication Methods */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-700">
          {t("clinicalSections.speech.communicationMethods")}
        </p>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="speaks-independently"
              checked={communicationMethods.speaksIndependently}
              onCheckedChange={(checked) =>
                onCommunicationMethodsChange(
                  "speaksIndependently",
                  checked as boolean,
                )
              }
            />
            <Label
              htmlFor="speaks-independently"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.speaksIndependently")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="aac-device"
              checked={communicationMethods.usesAACDevice}
              onCheckedChange={(checked) =>
                onCommunicationMethodsChange(
                  "usesAACDevice",
                  checked as boolean,
                )
              }
            />
            <Label
              htmlFor="aac-device"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.usesAacDevice")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="uses-gestures"
              checked={communicationMethods.usesGestures}
              onCheckedChange={(checked) =>
                onCommunicationMethodsChange("usesGestures", checked as boolean)
              }
            />
            <Label
              htmlFor="uses-gestures"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.speech.usesGestures")}
            </Label>
          </div>
        </div>
      </div>
    </div>
  );
};
